import React, { useMemo } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useGetShareAction } from './getAction';

type ShareAction<CH extends ShareChannel = ShareChannel> = {
	channel: CH;
	url: string;
	shortUrl?: string;
	action: () => Promise<boolean>;
	ShareAction: <P extends { onClick?: () => void }>(props: { children: React.ReactElement<P> }) => React.JSX.Element;
};

type Options<CH extends ShareChannel = ShareChannel> = {
	config: ShareConfig;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getShortUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useShareActions<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{ getUrl, ...options }: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const getShareAction = useGetShareAction(options);
	const { getShortUrl } = options;
	return useMemo(
		() =>
			channels.map((channel) => {
				const url = getUrl(channel);
				const shortUrl = getShortUrl(channel);
				const shareActionFn = getShareAction(channel);

				return {
					channel,
					url,
					shortUrl: url === shortUrl ? undefined : shortUrl,
					action: shareActionFn,
					ShareAction: ({ children }: { children: React.ReactElement<{ onClick?: () => void }> }) => {
						return React.cloneElement(children, {
							...children.props,
							onClick: () => {
								void shareActionFn();
							},
						});
					},
				};
			}),
		[channels, getShareAction, getUrl, getShortUrl],
	);
}
